import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {InteractiveListItem} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  SearchSuggestionsBaseProps,
  SuggestionSelectHandler,
} from '../../../../../../srcCommon/inboxes/search/searchSuggestionsHelpers';
import {SearchSuggestionsDropdownItemContent} from './searchSuggestionsDropdownItemContent';
import {SearchSuggestionsDropdownItemIcon} from './searchSuggestionsDropdownItemIcon';
import {SearchSuggestionsDropdownSection} from './searchSuggestionsDropdownSection';

/*
 * Constants.
 */

const searchModifiersSections: ReadonlyArray<ModifierSection> = [
  {
    title: <Trans id="search-header-inbox">Inbox</Trans>,
    icon: 'inboxSmall',
    modifiers: [[ModifiersEnum.INBOX]],
  },
  {
    title: <Trans id="search-header-message">Message</Trans>,
    icon: 'messageSmall',
    modifiers: [
      [ModifiersEnum.RECIPIENT],
      [ModifiersEnum.FROM],
      [ModifiersEnum.TO],
      [ModifiersEnum.CC],
      [ModifiersEnum.BCC],
      [ModifiersEnum.SUBJECT, <Trans id="search-modifier-subject-description">&quot;text&quot;</Trans>],
      [ModifiersEnum.BODY, <Trans id="search-modifier-body-description">&quot;text&quot;</Trans>],
    ],
  },
  {
    title: <Trans id="search-header-teammates">Teammates</Trans>,
    icon: 'teammateSmall',
    modifiers: [
      [ModifiersEnum.ASSIGNEE],
      [ModifiersEnum.AUTHOR],
      [ModifiersEnum.PARTICIPANT],
      [ModifiersEnum.MENTION],
      [ModifiersEnum.COMMENTER],
    ],
  },
  {
    title: <Trans id="search-header-properties">Properties</Trans>,
    icon: 'searchSmall',
    modifiers: [
      [
        ModifiersEnum.TYPE,
        <Trans id="search-modifier-type-description">email, tweet, sms, facebook...</Trans>,
      ],
      [ModifiersEnum.TAG, <Trans id="search-modifier-tag-description">tag_name</Trans>],
      [ModifiersEnum.ACCOUNT, <Trans id="search-modifier-tag-account">account_name</Trans>],
      [ModifiersEnum.HAS, <Trans id="search-modifier-has-description">attachment, no-tags...</Trans>],
      [
        ModifiersEnum.IS,
        <Trans id="search-modifier-is-description">unread, unreplied, open, closed, snoozed...</Trans>,
      ],
      [ModifiersEnum.IN, <Trans id="search-modifier-in-description">comments, messages, attachments</Trans>],
      [ModifiersEnum.BEFORE, <Trans id="search-modifier-date-description">yyyy-mm-dd</Trans>],
      [ModifiersEnum.AFTER, <Trans id="search-modifier-date-description">yyyy-mm-dd</Trans>],
      [ModifiersEnum.DURING, <Trans id="search-modifier-date-description">yyyy-mm-dd</Trans>],
    ],
  },
];

/*
 * Props.
 */

interface ModifierSection {
  title: ReactNode;
  icon: IconName;
  modifiers: ReadonlyArray<[ModifiersEnum] | [ModifiersEnum, ReactNode]>;
}

interface InboxesSearchSuggestionsDropdownModifiersProps extends SearchSuggestionsBaseProps {}

/*
 * Styles.
 */

const StyledSectionWrapperDiv = styled.div`
  margin-top: 6px;
`;

const StyledSectionDiv = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr;
  grid-column-gap: 8px;
  grid-row-gap: 6px;
  grid-template-areas:
    'icon header'
    '. modifiers';

  padding: 0 16px 15px;
`;

const StyledHeaderDiv = styled.div`
  grid-area: header;

  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
`;

const StyledModifiersDiv = styled.div`
  grid-area: modifiers;

  display: flex;
  flex-wrap: wrap;
  grid-row-gap: 10px;
  grid-column-gap: 12px;
`;

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownModifiers: FC<
  InboxesSearchSuggestionsDropdownModifiersProps
> = ({onSuggestionSelect}) => (
  <SearchSuggestionsDropdownSection title={<Trans id="inboxes-advanced-filters">Advanced Filters</Trans>}>
    <StyledSectionWrapperDiv>
      {searchModifiersSections.map((section) => (
        <StyledSectionDiv key={section.icon}>
          <SearchSuggestionsDropdownItemIcon iconName={section.icon} />
          <StyledHeaderDiv>{section.title}</StyledHeaderDiv>
          <StyledModifiersDiv>{renderSectionModifiers(section, onSuggestionSelect)}</StyledModifiersDiv>
        </StyledSectionDiv>
      ))}
    </StyledSectionWrapperDiv>
  </SearchSuggestionsDropdownSection>
);

/*
 * Helpers.
 */

function renderSectionModifiers(section: ModifierSection, onSuggestionSelect: SuggestionSelectHandler) {
  return (
    <>
      {section.modifiers.map(([modifier, placeholder], index) =>
        renderSearchModifierItem(index, modifier, placeholder, onSuggestionSelect),
      )}
    </>
  );
}

function renderSearchModifierItem(
  index: number,
  modifier: ModifiersEnum,
  placeholder: ReactNode | undefined,
  onSuggestionSelect: SuggestionSelectHandler,
) {
  const value = `${modifier}:`;

  return (
    <InteractiveListItem
      key={index}
      onSelect={(_e, key) =>
        onSuggestionSelect(value, key, {insertWhiteSpace: false, modifierHelperType: modifier})
      }
    >
      {({itemProps, isHighlighted}) => (
        <SearchSuggestionsDropdownItemContent
          {...itemProps}
          value={value}
          valuePlaceholder={placeholder}
          isHighlighted={isHighlighted}
          shouldHighlightSearchTokens
        />
      )}
    </InteractiveListItem>
  );
}
